import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  PlaneTakeoff, PlaneLanding, Palmtree,
  Ticket, Landmark, Trash2, Plus,
  Clock, ShieldCheck, AlertCircle,
  Plane, MoveRight
} from "lucide-react";

const createEmptyFlight = () => ({
  fromAirport: "",
  toAirport: "",
  airline: "",
  ticketNumber: "",
  transit: "",
  airportCharges: "",
});

const FlightForm = () => {
  const [flights, setFlights] = useState([createEmptyFlight()]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    fetchFlights();
  }, []);

  const fetchFlights = async () => {
    setLoading(true);
    try {
      const res = await makeRequestAuth("get", "/user/getFlight");
      if (res?.data?.flights?.length) {
        setFlights(res.data.flights);
        setPreview(res.data);
      }
    } catch {
      console.log("No flight data found");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    setFlights((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addFlight = () => setFlights((prev) => [...prev, createEmptyFlight()]);
  const removeFlight = (index) => setFlights((prev) => prev.filter((_, i) => i !== index));

  const validateFlights = () => {
    let list = [];
    flights.forEach((f, i) => {
      const row = i + 1;
      if (!f.fromAirport.trim()) list.push(`Flight ${row}: From Airport is required`);
      if (!f.toAirport.trim()) list.push(`Flight ${row}: To Airport is required`);
      if (!f.airline.trim()) list.push(`Flight ${row}: Airline is required`);
      if (!f.ticketNumber.trim()) list.push(`Flight ${row}: Ticket Number is required`);
      if (!f.transit.trim()) list.push(`Flight ${row}: Transit is required`);
      if (f.airportCharges === "" || isNaN(f.airportCharges) || Number(f.airportCharges) < 0)
        list.push(`Flight ${row}: Valid Airport Charges required`);
    });
    return list;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateFlights();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo(0, 0);
      return;
    }
    setErrors([]);
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/user/createOrUpdateFlight", { flights });
      setPreview(res?.data);
      showSuccessToast(res?.message || "Flight details saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { bg: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} /> },
    approve: { bg: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <ShieldCheck size={14} /> },
    reject: { bg: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} /> },
  };

  return (
    <div className="max-w-4xl mx-auto py-4 antialiased font-normal text-slate-700">
      <ToastContainer />

      {/* Modern Loader */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <p className="text-[#132b45] font-medium uppercase tracking-wider text-[10px]">Syncing Air Records...</p>
          </div>
        </div>
      )}

      {/* ERROR BOX */}
      {errors.length > 0 && (
        <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-2xl animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
            <AlertCircle size={20} /> <span className="text-sm font-semibold">Submission Errors:</span>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            {errors.map((e, i) => (
              <li key={i} className="text-rose-600 text-[11px] py-0.5 font-normal">• {e}</li>
            ))}
          </ul>
        </div>
      )}

      {preview ? (
        /* ================= PREVIEW SECTION ================= */
        <div className="space-y-6 animate-in fade-in duration-500">
          <div className="flex justify-between items-center bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
            <div>
              <h2 className="text-2xl font-medium text-[#132b45]">Flight Itinerary</h2>
              <p className="text-slate-400 text-xs font-normal uppercase tracking-wider">Declared Air Travel History</p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full border font-medium text-[10px] uppercase tracking-wider ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.bg || "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon}
              {preview?.status || "Pending"}
            </div>
          </div>

          <div className="grid gap-4">
            {preview.flights.map((f, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-[2rem] overflow-hidden shadow-sm hover:shadow-md transition-all">
                <div className="bg-slate-50 px-8 py-4 border-b border-slate-100 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-medium text-indigo-600 uppercase">{f.fromAirport}</span>
                    <MoveRight className="text-slate-300" size={20} />
                    <span className="text-lg font-medium text-indigo-600 uppercase">{f.toAirport}</span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Flight #{i + 1}</span>
                </div>
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <PreviewItem label="Airline" value={f.airline} icon={<Plane size={14} />} />
                  <PreviewItem label="Ticket #" value={f.ticketNumber} icon={<Ticket size={14} />} />
                  <PreviewItem label="Transit" value={f.transit} icon={<Palmtree size={14} />} />
                  <PreviewItem label="Charges" value={`$${f.airportCharges}`} icon={<Landmark size={14} />} />
                </div>
              </div>
            ))}
          </div>

          {preview.status === "reject" && (
            <div className="flex justify-center pt-4">
              <button onClick={() => { setPreview(null); setFlights([createEmptyFlight()]); }} className="bg-[#132b45] hover:bg-black text-white px-10 py-4 rounded-2xl font-normal text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg flex items-center gap-2">
                Update Information
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ================= FORM SECTION ================= */
        <form onSubmit={handleSubmit} className="space-y-8 animate-in slide-in-from-bottom-8 duration-700">
          <div className="flex items-center gap-4 px-2">
            <div className="p-3 bg-[#132b45] text-white rounded-2xl shadow-lg shadow-blue-200"><Plane size={28} /></div>
            <div>
              <h1 className="text-2xl font-medium text-[#132b45]">Flight Details</h1>
              <p className="text-slate-400 text-xs font-normal uppercase tracking-wider">Provide accurate ticket information</p>
            </div>
          </div>

          <div className="space-y-10">
            {flights.map((flight, index) => (
              <div key={index} className="bg-white rounded-[2.5rem] border border-slate-200 p-8 shadow-sm relative group hover:border-indigo-200 transition-all">
                <div className="flex justify-between items-center mb-8">
                  <h3 className="flex items-center gap-2 font-medium text-[#132b45] text-xs uppercase tracking-wider">
                    <span className="w-8 h-8 rounded-xl bg-slate-900 text-white flex items-center justify-center text-[10px] font-normal">{index + 1}</span>
                    Sector Details
                  </h3>
                  {flights.length > 1 && (
                    <button type="button" onClick={() => removeFlight(index)} className="text-rose-500 hover:bg-rose-50 p-2 rounded-xl transition-all flex items-center gap-1 text-[10px] font-medium uppercase tracking-tighter">
                      <Trash2 size={16} /> Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <Input label="From Airport" icon={<PlaneTakeoff size={14} />} placeholder="DXB, LHR, DEL..." value={flight.fromAirport} onChange={(v) => handleChange(index, "fromAirport", v)} />
                  <Input label="To Airport" icon={<PlaneLanding size={14} />} placeholder="JFK, SIN, SYD..." value={flight.toAirport} onChange={(v) => handleChange(index, "toAirport", v)} />
                  <Input label="Airline" icon={<Plane size={14} />} placeholder="Emirates, Qatar, Air India..." value={flight.airline} onChange={(v) => handleChange(index, "airline", v)} />
                  <Input label="Ticket Number" icon={<Ticket size={14} />} placeholder="ETKT 123456789" value={flight.ticketNumber} onChange={(v) => handleChange(index, "ticketNumber", v)} />
                  <Input label="Transit Point" icon={<Palmtree size={14} />} placeholder="Layover City (e.g. Doha)" value={flight.transit} onChange={(v) => handleChange(index, "transit", v)} />
                  <Input type="number" label="Airport Charges" icon={<Landmark size={14} />} placeholder="0.00" value={flight.airportCharges} onChange={(v) => handleChange(index, "airportCharges", v)} />
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-4 px-2 pt-4">
            <button type="button" onClick={addFlight} className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-slate-200 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 py-5 rounded-[2rem] font-medium text-xs uppercase tracking-wider transition-all active:scale-95">
              <Plus size={20} /> Add Another Flight
            </button>
            <button type="submit" disabled={loading} className="flex-1 bg-[#132b45] hover:bg-black text-white py-5 rounded-[2rem] font-medium text-xs uppercase tracking-wider shadow-xl shadow-blue-900/10 transition-all active:scale-95">
              {loading ? "Saving Details..." : "Confirm Flight Records"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

/* --- HELPER COMPONENTS --- */

const Input = ({ label, value, onChange, type = "text", icon, placeholder }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wider ml-1 flex items-center gap-2">
      {icon} {label} *
    </label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 focus:border-indigo-500 focus:bg-white outline-none transition-all font-medium text-[#132b45] placeholder:text-slate-300 shadow-sm"
    />
  </div>
);

const PreviewItem = ({ label, value, icon }) => (
  <div className="flex gap-3">
    <div className="mt-1 h-8 w-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400 shrink-0">{icon}</div>
    <div className="overflow-hidden">
      <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider block mb-0.5">{label}</span>
      <p className="font-medium text-[#132b45] text-sm leading-tight truncate">{value || "-"}</p>
    </div>
  </div>
);

export default FlightForm;